"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.TerminalProfileBindRequest = exports.CountryPackEnableRequest = exports.CountryPackSummary = exports.CountryPackManifest = exports.PackMetadata = exports.PackCertification = exports.FiscalAdapterBinding = exports.ReportingFormats = exports.ReportingFormat = exports.AgeRestrictions = exports.AgeRestrictionCategory = exports.MandatoryFiscalText = exports.MandatoryFiscalTextBlock = exports.ReceiptTemplate = exports.ReceiptTemplateElement = exports.ConfigurableField = exports.TaxCatalogue = exports.TaxCategoryDef = exports.Semver = exports.PackId = exports.Iso4217 = exports.Iso3166Alpha2 = void 0;
const zod_1 = require("zod");
/**
 * Canonical country-pack contracts. Mirrors the YAML shape declared
 * in country-packs/shared/schema/pack.schema.json (and its sibling
 * schemas) in zod, with snake_case → camelCase mapping at the TS edge
 * for in-process consumption. The denormalised projection lives in
 * `country_packs.manifest` (Json column) at runtime; this is the
 * authoritative TypeScript shape for it.
 *
 * See apps/bos/docs/modules/country-packs.md for the runtime model.
 */
exports.Iso3166Alpha2 = zod_1.z
    .string()
    .regex(/^[A-Z]{2}$/, 'two-letter ISO-3166-1 alpha-2 (uppercase)');
exports.Iso4217 = zod_1.z.string().regex(/^[A-Z]{3}$/, 'three-letter ISO-4217 (uppercase)');
exports.PackId = zod_1.z
    .string()
    .regex(/^[a-z]{2}(-[a-z0-9]+)*$/, 'lowercase ISO-3166-1 alpha-2 + optional dash qualifiers');
exports.Semver = zod_1.z.string().regex(/^\d+\.\d+\.\d+$/, 'X.Y.Z semver');
// ---------- Pack-internal sub-shapes (mirror sibling YAML files) ----------
exports.TaxCategoryDef = zod_1.z.object({
    id: zod_1.z.string().regex(/^[a-z][a-z0-9_]*$/),
    displayName: zod_1.z.string().min(1),
    allowedRates: zod_1.z.array(zod_1.z.number().min(0).max(100)).min(1),
    defaultRate: zod_1.z.number().min(0).max(100).optional(),
});
exports.TaxCatalogue = zod_1.z.object({
    defaultCurrency: exports.Iso4217.optional(),
    categories: zod_1.z.array(exports.TaxCategoryDef).min(1),
});
const ConfigurableFieldBool = zod_1.z.object({
    type: zod_1.z.literal('bool'),
    default: zod_1.z.boolean().optional(),
    description: zod_1.z.string().optional(),
});
const ConfigurableFieldString = zod_1.z
    .object({
    type: zod_1.z.literal('string'),
    default: zod_1.z.string().optional(),
    description: zod_1.z.string().optional(),
    maxLength: zod_1.z.number().int().positive().optional(),
})
    // H-55: cross-field — default must respect maxLength.
    .superRefine((v, ctx) => {
    if (v.default !== undefined && v.maxLength !== undefined && v.default.length > v.maxLength) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            message: `default "${v.default}" exceeds maxLength ${v.maxLength}`,
            path: ['default'],
        });
    }
});
const ConfigurableFieldNumber = zod_1.z
    .object({
    type: zod_1.z.literal('number'),
    default: zod_1.z.number().optional(),
    description: zod_1.z.string().optional(),
    min: zod_1.z.number().optional(),
    max: zod_1.z.number().optional(),
})
    // H-55: cross-field — min ≤ max, default in [min,max] if both
    // are declared.
    .superRefine((v, ctx) => {
    if (v.min !== undefined && v.max !== undefined && v.min > v.max) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            message: `min ${v.min} exceeds max ${v.max}`,
            path: ['min'],
        });
    }
    if (v.default !== undefined && v.min !== undefined && v.default < v.min) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            message: `default ${v.default} below min ${v.min}`,
            path: ['default'],
        });
    }
    if (v.default !== undefined && v.max !== undefined && v.default > v.max) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            message: `default ${v.default} above max ${v.max}`,
            path: ['default'],
        });
    }
});
const ConfigurableFieldEnum = zod_1.z
    .object({
    type: zod_1.z.literal('enum'),
    values: zod_1.z.array(zod_1.z.string()).min(2),
    default: zod_1.z.string().optional(),
    description: zod_1.z.string().optional(),
})
    // H-55: cross-field — default must be in the declared values.
    .superRefine((v, ctx) => {
    if (v.default !== undefined && !v.values.includes(v.default)) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            message: `default "${v.default}" not in values [${v.values.join(', ')}]`,
            path: ['default'],
        });
    }
});
// `z.union` rather than `z.discriminatedUnion` so the per-variant
// `superRefine` cross-field checks (H-55) are accepted —
// ZodEffects can't be discriminated members.
exports.ConfigurableField = zod_1.z.union([
    ConfigurableFieldBool,
    ConfigurableFieldString,
    ConfigurableFieldNumber,
    ConfigurableFieldEnum,
]);
exports.ReceiptTemplateElement = zod_1.z.object({
    id: zod_1.z.string().regex(/^[a-z][a-z0-9_]*$/),
    locked: zod_1.z.boolean(),
    description: zod_1.z.string().optional(),
    configurableFields: zod_1.z.record(zod_1.z.string(), exports.ConfigurableField).optional(),
});
exports.ReceiptTemplate = zod_1.z.object({
    elements: zod_1.z.array(exports.ReceiptTemplateElement).min(1),
});
const ReceiptType = zod_1.z.enum(['sale', 'refund', 'void', 'parked', 'x_report', 'z_report', 'copy']);
exports.MandatoryFiscalTextBlock = zod_1.z.object({
    id: zod_1.z.string().regex(/^[a-z][a-z0-9_]*$/),
    /**
     * Optional dotted key (e.g. `receipt.return.type`) that the terminal
     * sources this label by, in place of its baked-in English fallback
     * (RFL-172). Distinct from `id`, which is underscore-only and may be
     * referenced by the receipt template's locked element ids — `key`
     * mirrors the terminal's `i18n.t(...)` lookup key so the resolved
     * manifest can drive the label without a string-munging convention.
     */
    key: zod_1.z
        .string()
        .regex(/^[a-z][a-z0-9_]*(\.[a-z0-9_]+)*$/)
        .optional(),
    text: zod_1.z.string().min(1),
    appliesTo: zod_1.z.array(ReceiptType).min(1).optional(),
    regulatoryReference: zod_1.z.string().optional(),
});
exports.MandatoryFiscalText = zod_1.z.object({
    /** Fixed by law for the country (e.g. 'lt', 'uk' — never user-translatable). */
    language: zod_1.z.string().regex(/^[a-z]{2,3}$/),
    blocks: zod_1.z.array(exports.MandatoryFiscalTextBlock).min(1),
});
exports.AgeRestrictionCategory = zod_1.z.object({
    id: zod_1.z.string().regex(/^[a-z][a-z0-9_]*$/),
    minimumAge: zod_1.z.number().int().min(0).max(99),
    regulatoryReference: zod_1.z.string().optional(),
});
exports.AgeRestrictions = zod_1.z.object({
    categories: zod_1.z.array(exports.AgeRestrictionCategory),
});
exports.ReportingFormat = zod_1.z.object({
    id: zod_1.z.string().regex(/^[a-z][a-z0-9_]*$/),
    kind: zod_1.z.enum(['z_report', 'x_report', 'periodic_submission', 'real_time_transmission']),
    encoding: zod_1.z.enum(['xml', 'json', 'csv', 'txt', 'binary']).optional(),
    schemaRef: zod_1.z.string().optional(),
    frequency: zod_1.z.enum(['per_shift', 'daily', 'monthly', 'on_demand', 'real_time']).optional(),
    regulatoryReference: zod_1.z.string().optional(),
});
exports.ReportingFormats = zod_1.z.object({
    formats: zod_1.z.array(exports.ReportingFormat).min(1),
});
// ---------- Pack manifest (denormalised) ----------
exports.FiscalAdapterBinding = zod_1.z.object({
    bindingId: zod_1.z.string().min(1),
    category: zod_1.z.enum(['A', 'B', 'none']).optional(),
    configSchemaRef: zod_1.z.string().optional(),
    /**
     * Country-level fiscal adapter configuration (RFL-166), e.g. the UA
     * PRRO DPS endpoint. Free-form: each adapter's terminal-side factory
     * documents the keys it consumes (RFL-168). Per-TERMINAL fiscal
     * identity (register fiscal number, КЕП key material) deliberately
     * does NOT live here — Cat B keys never leave the terminal; the
     * per-terminal lifecycle is RFL-167.
     */
    config: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()).optional(),
});
exports.PackCertification = zod_1.z.object({
    type: zod_1.z.enum(['declarative', 'device-certified', 'software-certified']),
    authority: zod_1.z.string().optional(),
    reference: zod_1.z.string().optional(),
});
exports.PackMetadata = zod_1.z
    .object({
    certification: exports.PackCertification.optional(),
    dataRetentionDays: zod_1.z.number().int().positive().optional(),
})
    .optional();
/**
 * The full denormalised pack content stored in `country_packs.manifest`.
 * Contains pack.yaml fields + every resolved sibling YAML payload.
 * Consumer modules (Tax Rates, Receipt Config, the receipt rendering
 * pipeline) read from this object — never the filesystem at runtime.
 */
exports.CountryPackManifest = zod_1.z
    .object({
    id: exports.PackId,
    version: exports.Semver,
    country: zod_1.z.object({
        iso3166Alpha2: exports.Iso3166Alpha2,
        displayName: zod_1.z.string().optional(),
    }),
    currency: zod_1.z.object({
        iso4217: exports.Iso4217,
        smallestUnit: zod_1.z.string().min(1),
        decimals: zod_1.z.number().int().min(0).max(4).default(2),
    }),
    fiscalAdapter: exports.FiscalAdapterBinding,
    mandatoryFiscalText: exports.MandatoryFiscalText,
    taxCatalogue: exports.TaxCatalogue,
    receiptTemplate: exports.ReceiptTemplate,
    ageRestrictions: exports.AgeRestrictions,
    reportingFormats: exports.ReportingFormats,
    metadata: exports.PackMetadata,
})
    .strict(); // H-73: reject unknown top-level manifest keys (defence-in-depth for direct safeParse callers; the loader merge produces exactly these keys)
// ---------- Admin REST shapes ----------
/**
 * Row returned by `GET /country-packs` and `GET /country-packs/:id`.
 * H-70: kept in sync with the raw Prisma `country_packs` row the
 * controller returns — `enabledBy`, `bundledFromPath`, `createdAt`,
 * `updatedAt` were previously omitted, drifting the contract from the
 * actual response (the admin UI reads `bundledFromPath`).
 */
exports.CountryPackSummary = zod_1.z.object({
    id: zod_1.z.string(),
    packId: exports.PackId,
    version: exports.Semver,
    country: exports.Iso3166Alpha2,
    displayName: zod_1.z.string(),
    enabled: zod_1.z.boolean(),
    isBundled: zod_1.z.boolean(),
    enabledAt: zod_1.z.string().datetime().nullable(),
    enabledBy: zod_1.z.string().nullable(),
    bundledFromPath: zod_1.z.string().nullable(),
    lastSeenAt: zod_1.z.string().datetime(),
    createdAt: zod_1.z.string().datetime(),
    updatedAt: zod_1.z.string().datetime(),
    manifest: exports.CountryPackManifest,
});
exports.CountryPackEnableRequest = zod_1.z
    .object({
    enabled: zod_1.z.boolean(),
})
    .strict(); // H-73: reject unknown request-body keys
exports.TerminalProfileBindRequest = zod_1.z
    .object({
    packId: exports.PackId,
})
    .strict(); // H-73: reject unknown request-body keys
//# sourceMappingURL=country-pack.js.map